import React, { useMemo, useEffect } from 'react';
import {
	Navigate,
	Outlet,
	useLocation,
	useNavigate,
	useNavigation,
	useParams,
} from 'react-router-dom';
import { Box, Stack, Skeleton, useTheme } from '@mui/material';
import { useObjectContext } from '@/hooks/useObjectContext';
import { useTabOrder } from '@/hooks/useTabOrder';
import { useDispatch } from 'react-redux';
import { setItemId, setParentItemId } from '@/features/table/attachmentsTableSlice';
import TabHeader from '../TabHeader';
import { FolioErrorBoundary } from '@/components/layouts/FolioErrorBoundary';
import { PageHeader } from '@/components/layouts/PageHeader';
import { PageTabsBar } from '@/components/layouts/PageHeader/PageTabsBar';
import { ReorderableTabs } from '@/components/layouts/ReorderableTabs';
import { getObjectTabPath } from '@/utils/navigation';
import { getPrimaryObject } from '@/utils/getPrimaryObject';

// Just moving this here in case we need it again
const BetaBanner = ({ label }) => {
	return (
		<Box
			sx={{
				display: 'flex',
				alignItems: 'center',
				position: 'relative',
				height: '100%',
			}}
		>
			{label}
			<Box
				sx={{
					position: 'absolute',
					top: '30%',
					right: '-3.25em',
					transform: 'translateY(-75%) rotate(45deg)',
					backgroundColor: '#34495E',
					color: 'white',
					padding: '2px 20px',
					fontSize: '0.7em',
					whiteSpace: 'nowrap',
				}}
			>
				{'BETA'}
			</Box>
		</Box>
	);
};

const LoadingComponentHeader = () => (
	<Box display='flex' sx={{ justifyContent: 'right' }}>
		<Skeleton animation='wave' variant='rounded' height='3em' width='8em' />
	</Box>
);

// Tabs are reorderable when an objectType is given ('client', 'deal', etc.), which also scopes
// where the order is persisted.
export const TabView = ({ tabs, subtitle, icon, objectType }) => {
	const theme = useTheme();
	const navigate = useNavigate();

	const params = useParams();
	const navigation = useNavigation();

	const { reorderable, tabOrder, displayTabs, persistOrder } = useTabOrder(objectType, tabs);
	const defaultTabValue = displayTabs?.[0]?.value ?? '';
	const tabValues = useMemo(() => displayTabs?.map((tab) => tab?.value) ?? [], [displayTabs]);

	const isLoading = navigation.state === 'loading';

	const isClient = Boolean(params?.clientId);
	const isRelationshipCompany = Boolean(params?.relationshipCompanyId);

	const location = useLocation();

	const dispatch = useDispatch();

	const { dealRow, clientRow, relationshipCompany } = useObjectContext();

	const rowId = useMemo(
		() =>
			getPrimaryObject({
				isRelationshipCompany,
				isClient,
				relationshipCompany,
				client: clientRow,
				deal: dealRow,
			})?.driveId,
		[
			isRelationshipCompany,
			isClient,
			dealRow,
			clientRow,
			relationshipCompany,
		],
	);

	const recordId = params?.dealId || params?.clientId || params?.relationshipCompanyId;
	const pathTab = location.pathname.split('/').pop();
	const isOnRecordRoot = pathTab === String(recordId);
	const rootTabPath = displayTabs.find((tab) => tab.value === '')?.alias;

	// The record root opens the first tab, so the root tab links to its alias once it has been moved.
	const toTabPath = (value) =>
		value === '' && defaultTabValue !== '' && rootTabPath ? rootTabPath : value;

	const currentTab = isOnRecordRoot
		? defaultTabValue
		: pathTab === rootTabPath
			? ''
			: tabValues.includes(pathTab)
				? pathTab
				: defaultTabValue;

	const handleTabChange = (event, value) => {
		dispatch(setParentItemId(rowId));
		dispatch(setItemId(rowId));
		navigate({
			pathname: getObjectTabPath(location.pathname, recordId, toTabPath(value)),
			search: location.search,
		});
	};

	useEffect(() => {
		window.scrollTo(0, 0);
	}, [currentTab]);

	// The record root renders the root tab, so redirect to the first tab instead of flashing it.
	const shouldOpenDefaultTab = isOnRecordRoot && defaultTabValue !== '';

	const CurrentHeaderComponent = displayTabs.find(
		(tab) => tab.value === currentTab,
	)?.HeaderComponent;

	return (
		<Box
			className='tab-view'
			sx={{
				mt: '1em',
			}}
		>
			<TabHeader {...{ subtitle, icon }} />
			<Stack
				direction='row'
				alignItems='center'
				sx={{ mt: 3, mb: 2, borderBottom: `solid 1px ${theme.palette.divider}` }}
			>
				<ReorderableTabs
					tabs={displayTabs}
					value={currentTab}
					reorderable={reorderable}
					onReorder={persistOrder}
					showReset={tabOrder.length > 0}
					droppableId='tab-view'
					getTabTo={(tab) => ({
						pathname: getObjectTabPath(
							location.pathname,
							recordId,
							toTabPath(tab.value),
						),
						search: location.search,
					})}
					onTabClick={handleTabChange}
				/>
			</Stack>
			<Box px={3}>
				{CurrentHeaderComponent && (
					<Stack
						spacing={1}
						direction='row'
						justifyContent={'flex-end'}
						alignItems={'center'}
					>
						{isLoading ? (
							<LoadingComponentHeader />
						) : (
							<>{CurrentHeaderComponent && <CurrentHeaderComponent />}</>
						)}
					</Stack>
				)}
				<FolioErrorBoundary
					dealId={params?.dealId}
					clientId={params?.clientId}
					key={`${params?.dealId ?? params?.clientId ?? params?.relationshipCompanyId}-${currentTab}`}
					scope={currentTab}
					message='Error loading tab'
					context='TabView'
				>
					{shouldOpenDefaultTab ? (
						<Navigate
							to={{ pathname: defaultTabValue, search: location.search }}
							replace
						/>
					) : (
						<Outlet />
					)}
				</FolioErrorBoundary>
			</Box>
		</Box>
	);
};
TabView.displayName = 'TabView';

export const NoContextTabView = ({
	tabs = [],
	icon,
	title,
	onTabChange = (tab) => {},
	actions,
}) => {
	const location = useLocation();
	const navigate = useNavigate();
	const tabValues = useMemo(() => tabs.map((tab) => tab?.value), [tabs]);
	const defaultTab = tabValues[0] ?? '';

	const pathSegments = useMemo(
		() => location.pathname.split('/').filter(Boolean),
		[location.pathname],
	);
	// Nested tab slug is only the final segment when the URL is deeper than the section root
	// (e.g. /unlayer-templates/ai-templates → "ai-templates", /unlayer-templates → "").
	const nestedTabSegment = pathSegments.length > 1 ? pathSegments[pathSegments.length - 1] : '';
	const currentTab = tabValues.includes(nestedTabSegment) ? nestedTabSegment : defaultTab;

	const handleTabChange = (event, value) => {
		if (value) {
			navigate(`${value}`, { relative: 'path' });
		} else {
			navigate('.', { relative: 'path' });
		}
		if (onTabChange) onTabChange(value);
	};

	useEffect(() => {
		window.scrollTo(0, 0);
	}, [currentTab]);

	useEffect(() => {
		if (tabs.length === 0) {
			return;
		}

		const hasUnknownNestedTab =
			pathSegments.length > 1 &&
			nestedTabSegment !== '' &&
			!tabValues.includes(nestedTabSegment);

		// e.g. /settings → /settings/user when the default tab has a non-empty slug
		const shouldRedirectToDefaultTab =
			pathSegments.length === 1 && defaultTab !== '' && nestedTabSegment !== defaultTab;

		if (hasUnknownNestedTab || shouldRedirectToDefaultTab) {
			if (defaultTab) {
				navigate(defaultTab, { replace: true, relative: 'path' });
			} else {
				navigate('.', { replace: true, relative: 'path' });
			}
			onTabChange(defaultTab);
		}
	}, [
		defaultTab,
		navigate,
		nestedTabSegment,
		onTabChange,
		pathSegments.length,
		tabValues,
		tabs.length,
	]);

	if (tabs.length === 0) {
		return null;
	}

	return (
		<Box
			className='no-context-tab-view'
			sx={{
				pt: 1,
			}}
		>
			<PageHeader title={title} icon={icon} />
			<PageTabsBar
				tabs={tabs}
				value={currentTab}
				onChange={handleTabChange}
				actions={actions}
			/>

			<Box px={3}>
				<FolioErrorBoundary
					key={currentTab}
					scope={currentTab}
					message='Error loading tab'
					context='NoContextTabView'
				>
					<Outlet />
				</FolioErrorBoundary>
			</Box>
		</Box>
	);
};
NoContextTabView.displayName = 'NoContextTabView';
